import React, { useRef, useState } from 'react';
import { Info } from 'lucide-react';
import cx from 'classnames';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';
import { Textarea } from '@ui/components/ui/textarea';

/** Long enough not to fire on a pointer crossing the cell, short enough to feel like hovering. */
const OPEN_AFTER = 120;

/** Leaving does not close it: an open panel takes the pointer off the mark, which reopened it in a loop. */
const DiscountNote = ({
  label,
  title,
  note,
  placeholder,
  onChange,
}: {
  /** What the mark is, for anything reading this rather than looking at it. */
  label: string;
  /** Which figure this is the reason for, at the head of the panel. */
  title: string;
  note: string;
  placeholder: string;
  /** Absent for a reader who may not change it: the reason is still theirs to read. */
  onChange?: (note: string) => void;
}): JSX.Element => {
  const [open, setOpen] = useState(false);
  const timer = useRef<number>();

  const openSoon = (): void => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), OPEN_AFTER);
  };
  // A pointer that crosses the mark and carries on has not asked for anything.
  const stopOpening = (): void => window.clearTimeout(timer.current);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Styled here rather than handed to `render`, which rebuilds the element and drops its handlers. */}
      <PopoverTrigger
        // The cell reads this to know which events are its own: see `ownEvent` in WorkforceGrid.
        data-discount-note=""
        aria-label={label}
        onPointerEnter={openSoon}
        onPointerLeave={stopOpening}
        // Its own control that happens to sit inside a cell that opens on click or focus.
        onClick={event => event.stopPropagation()}
        onFocus={event => event.stopPropagation()}
        className={cx(
          'inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          // A written note marks itself, so a column can be read without opening any of them.
          note.trim() === '' ? 'text-muted-foreground/50 hover:text-foreground' : 'text-foreground',
        )}
      >
        <Info className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent data-discount-note="" align="end" side="top" className="w-64 gap-1.5">
        <p className="text-xs font-medium text-foreground">{title}</p>
        {onChange ? (
          <Textarea
            value={note}
            placeholder={placeholder}
            aria-label={label}
            onChange={event => onChange(event.target.value)}
            className="min-h-16 bg-popover text-sm"
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{note}</p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default DiscountNote;
